import React, { useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AuthModal from './auth/AuthModal';
import { LogIn, UserCircle, Menu, X } from 'lucide-react';

const Header = () => {
    const { user, signOut } = useAuth();
    const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    const activeLinkStyle = {
        color: '#0D9488', // accent-teal
        fontWeight: '600',
    };

    const navLinkClass = "px-3 py-2 rounded-md text-sm font-medium text-main-text hover:text-accent-teal transition-colors";
    const mobileNavLinkClass = "block px-3 py-2 rounded-md text-base font-medium text-heading-text hover:text-accent-teal hover:bg-warm-off-white";

    // Component for mobile navigation links to keep code clean
    const MobileNavLinks = () => (
        <>
            <NavLink to="/discover" end style={({ isActive }) => isActive ? activeLinkStyle : undefined} className={mobileNavLinkClass} onClick={() => setIsMobileMenuOpen(false)}>Discover</NavLink>
            <NavLink to="/" end style={({ isActive }) => isActive ? activeLinkStyle : undefined} className={mobileNavLinkClass} onClick={() => setIsMobileMenuOpen(false)}>Feed</NavLink>
            <NavLink to="/events" style={({ isActive }) => isActive ? activeLinkStyle : undefined} className={mobileNavLinkClass} onClick={() => setIsMobileMenuOpen(false)}>Events</NavLink>
            <NavLink to="/videos" style={({ isActive }) => isActive ? activeLinkStyle : undefined} className={mobileNavLinkClass} onClick={() => setIsMobileMenuOpen(false)}>Video Insights</NavLink>
            <NavLink to="/aifoundry" style={({ isActive }) => isActive ? activeLinkStyle : undefined} className={mobileNavLinkClass} onClick={() => setIsMobileMenuOpen(false)}>AIFoundry</NavLink>
            <a href="https://qubitlyventures.com/en/blog" target="_blank" rel="noopener noreferrer" className={mobileNavLinkClass} onClick={() => setIsMobileMenuOpen(false)}>Articles</a>
        </>
    );

    return (
        <>
            <header className="bg-card-bg/80 backdrop-blur-sm shadow-md sticky top-0 z-40">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <Link to="/" className="text-2xl font-bold text-accent-teal">
                            AI Brahmi
                        </Link>

                        {/* --- DESKTOP NAVIGATION --- */}
                        <nav className="hidden md:flex items-center space-x-2">
                            <NavLink to="/discover" end style={({ isActive }) => isActive ? activeLinkStyle : undefined} className={navLinkClass}>Discover</NavLink>
                            <NavLink to="/" end style={({ isActive }) => isActive ? activeLinkStyle : undefined} className={navLinkClass}>Feed</NavLink>
                            <NavLink to="/events" style={({ isActive }) => isActive ? activeLinkStyle : undefined} className={navLinkClass}>Events</NavLink>
                            <NavLink to="/videos" style={({ isActive }) => isActive ? activeLinkStyle : undefined} className={navLinkClass}>Video Insights</NavLink>
                            <NavLink to="/aifoundry" style={({ isActive }) => isActive ? activeLinkStyle : undefined} className={navLinkClass}>AIFoundry</NavLink>
                            {/* Insights dropdown */}
                            <div className="relative group">
                                <button className={navLinkClass + " flex items-center gap-1"}>Insights <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg></button>
                                <div className="absolute left-0 top-full mt-1 w-40 bg-card-bg rounded-md shadow-lg py-1 z-50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none group-hover:pointer-events-auto">
                                    <a href="https://qubitlyventures.com/en/blog" target="_blank" rel="noopener noreferrer" className="block px-4 py-2 text-sm text-main-text hover:text-accent-teal hover:bg-warm-off-white">Articles</a>
                                </div>
                            </div>
                        </nav>

                        <div className="hidden md:flex items-center space-x-3">
                            {user ? (
                                <div className="relative group">
                                    <button className="p-1 bg-accent-teal text-white rounded-full flex items-center justify-center w-9 h-9 text-base font-semibold">
                                        {user.email ? user.email.charAt(0).toUpperCase() : <UserCircle size={22} />}
                                    </button>
                                    <div className="absolute right-0 top-full w-48 bg-card-bg rounded-md shadow-lg py-1 z-50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none group-hover:pointer-events-auto">
                                        <Link to="/profile" className="block px-4 py-2 text-sm text-main-text hover:bg-warm-off-white">My Profile</Link>
                                        <button
                                            onClick={signOut}
                                            className="w-full text-left block px-4 py-2 text-sm text-main-text hover:bg-warm-off-white"
                                        >
                                            Logout
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <button
                                    onClick={() => setIsAuthModalOpen(true)}
                                    className="bg-accent-teal text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-opacity-90 transition-colors flex items-center"
                                >
                                    <LogIn size={16} className="mr-1.5" /> Login / Sign Up
                                </button>
                            )}
                        </div>
                        <div className="md:hidden flex items-center">
                            <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="p-2 text-main-text hover:text-accent-teal">
                                {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
                            </button>
                        </div>
                    </div>
                </div>

                {/* --- MOBILE MENU --- */}
                {isMobileMenuOpen && (
                    <div className="md:hidden bg-card-bg shadow-lg">
                        <nav className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
                            <MobileNavLinks />
                            <div className="border-t border-slate-200 my-2"></div>
                            {user ? (
                                <>
                                    <Link to="/profile" onClick={() => setIsMobileMenuOpen(false)} className="w-full text-left block px-3 py-2 rounded-md text-base font-medium text-main-text hover:bg-warm-off-white">
                                        My Profile
                                    </Link>
                                    <button onClick={() => { signOut(); setIsMobileMenuOpen(false); }} className="w-full text-left block px-3 py-2 rounded-md text-base font-medium text-main-text hover:bg-warm-off-white">
                                        Logout
                                    </button>
                                </>
                            ) : (
                                <button onClick={() => { setIsAuthModalOpen(true); setIsMobileMenuOpen(false); }} className="w-full text-left flex items-center px-3 py-2 rounded-md text-base font-medium text-accent-teal hover:bg-warm-off-white">
                                    <LogIn size={18} className="mr-2" /> Login / Sign Up
                                </button>
                            )}
                        </nav>
                    </div>
                )}
            </header>
            <AuthModal isOpen={isAuthModalOpen && !user} onClose={() => setIsAuthModalOpen(false)} />
        </>
    );
};

export default Header;
